import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { submitTraining } from "@/lib/api";
import Assessment from "@/components/site/Assessment";

const services = [
  { id: "phone", name: "Phone Consultation", price: "$95 / 30 min", for: "New owners seeking quick guidance", change: "A clear plan, direction, and next step." },
  { id: "evaluation", name: "In-Home Evaluation", price: "From $250", for: "Households needing a full behavior read", change: "Written plan, service match, and pricing." },
  { id: "private", name: "Private Dog Training", price: "From $250 / session", for: "One-on-one obedience & lifestyle work", change: "Reliable commands, calmer walks, better home life." },
  { id: "puppy", name: "Puppy Boot Camp", price: "From $1,200 / 2 wks", for: "Pups 8–20 weeks — the imprint window", change: "House-manners, socialization and foundation." },
  { id: "bootcamp", name: "Basic Obedience Boot Camp", price: "From $2,800 / 2 wks", for: "Adult dogs needing structure & focus", change: "Leash mastery, off-leash discipline, public composure." },
  { id: "rehab", name: "Behavioral Rehabilitation", price: "From $3,800 / 3 wks", for: "Reactive, aggressive, or resource-guarding dogs", change: "Safer household, restored trust, new baseline." },
  { id: "service", name: "Service Dog Training", price: "Custom quote", for: "Veterans and individuals needing task work", change: "Trained partner for PTSD, mobility, medical alert." },
  { id: "board", name: "Boarding & Training", price: "From $180 / day", for: "Owners needing immersion + safe care", change: "Full lifestyle training under supervision." },
  { id: "ttt", name: "Train-the-Trainer Certification", price: "Application only", for: "Aspiring professional dog trainers", change: "The Wolfkeeper system, credentialed and mentored." },
  { id: "corporate", name: "Corporate & Community Dog Programs", price: "Custom", for: "Cities, shelters, businesses, workforce", change: "Group programs, workforce pathways, community impact." },
];

export default function DogTraining() {
  const [selected, setSelected] = useState("private");
  const [asmOpen, setAsmOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", dog_name: "", dog_breed: "", program: "private", message: "" });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { document.title = "Dog Training — Toriano Sanzone · Wolfkeeper University"; window.scrollTo(0, 0); }, []);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await submitTraining({ ...form, program: selected });
      toast.success("Booking received. Toriano's team will reach out within 24 hours.");
      setForm({ ...form, name: "", phone: "", email: "", dog_name: "", dog_breed: "", message: "" });
    } catch { toast.error("Something went wrong. Please call 312-933-1528."); }
    finally { setLoading(false); }
  };

  return (
    <div className="pt-[120px] md:pt-[140px]" data-testid="dog-training-page">
      {/* Header */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-12">
        <div className="text-eyebrow text-gold mb-4">Wolfkeeper University</div>
        <h1 className="font-display text-[clamp(2.6rem,8vw,8rem)] leading-[0.9] tracking-[-0.03em] max-w-5xl">
          Chicago's most trusted <span className="italic gold-foil">dog training.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-bone/75 font-editorial text-lg">
          Third-generation craftsmanship. Concierge intake. Ten programs — from a quick phone consultation to service-dog training and Train-the-Trainer certification.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#booking" className="btn-gold" data-testid="dt-cta-book">Book Evaluation</a>
          <button onClick={() => setAsmOpen(true)} className="btn-outline" data-testid="dt-cta-assessment">Take the 3-min Assessment</button>
          <a href="tel:3129331528" className="text-hair text-bone/70 hover:text-gold self-center">or call 312.933.1528</a>
        </div>
      </section>

      {/* Services grid */}
      <section id="services" className="section pt-8">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-2 gap-4">
            {services.map((s, i) => (
              <motion.button
                key={s.id}
                onClick={() => setSelected(s.id)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.03 * i }}
                className={`text-left border ${selected === s.id ? "border-gold bg-gradient-to-b from-gold/5 to-transparent" : "border-hair"} p-8 hover:border-gold transition-colors`}
                data-testid={`dt-service-${s.id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="text-hair text-gold">0{i + 1}</div>
                  <div className="text-hair text-bone/50">{s.price}</div>
                </div>
                <h3 className="font-display text-2xl md:text-3xl leading-tight">{s.name}</h3>
                <div className="mt-4 text-hair text-bone/50">For</div>
                <p className="text-bone/85 text-sm">{s.for}</p>
                <div className="mt-3 text-hair text-bone/50">Expected transformation</div>
                <p className="text-bone/85 text-sm italic">{s.change}</p>
                <div className={`mt-5 text-hair ${selected === s.id ? "text-gold" : "text-bone/40"}`}>{selected === s.id ? "✦ SELECTED — scroll to book" : "SELECT →"}</div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Assessment CTA */}
      <section id="assessment" className="section pt-0">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="border border-gold/30 p-10 md:p-14 bg-gradient-to-br from-gold/5 to-transparent">
            <div className="text-eyebrow text-gold mb-3">Free assessment</div>
            <h2 className="font-display text-3xl md:text-5xl leading-tight max-w-3xl">Is your dog trained — or has your dog <span className="italic gold-foil">trained you?</span></h2>
            <button onClick={() => setAsmOpen(true)} className="btn-gold mt-6" data-testid="dt-assessment-open">Take the free 3-minute assessment</button>
          </div>
        </div>
      </section>

      {/* Booking form */}
      <section id="booking" className="section pt-0">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 border-t border-hair pt-16">
            <div className="md:col-span-5">
              <div className="text-eyebrow text-gold">Book · Evaluation</div>
              <h3 className="font-display text-4xl md:text-5xl mt-4 leading-tight">Reserve your <span className="italic gold-foil">first session.</span></h3>
              <p className="mt-6 text-bone/70 font-editorial">Complete the intake and Toriano's team will confirm within 24 hours. Or call/text <a href="tel:3129331528" className="text-gold">312-933-1528</a>.</p>
            </div>
            <form onSubmit={submit} className="md:col-span-7 grid md:grid-cols-2 gap-6" data-testid="training-form">
              <div className="field"><label>Full name</label><input required value={form.name} onChange={upd("name")} data-testid="training-name"/></div>
              <div className="field"><label>Phone</label><input required value={form.phone} onChange={upd("phone")} data-testid="training-phone"/></div>
              <div className="field md:col-span-2"><label>Email</label><input required type="email" value={form.email} onChange={upd("email")} data-testid="training-email"/></div>
              <div className="field"><label>Dog&apos;s name</label><input value={form.dog_name} onChange={upd("dog_name")} data-testid="training-dog-name"/></div>
              <div className="field"><label>Breed</label><input value={form.dog_breed} onChange={upd("dog_breed")} data-testid="training-dog-breed"/></div>
              <div className="field md:col-span-2"><label>Program</label>
                <select value={selected} onChange={(e)=>setSelected(e.target.value)} data-testid="training-program">
                  {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="field md:col-span-2"><label>Tell us about your dog</label><textarea value={form.message} onChange={upd("message")} data-testid="training-message"/></div>
              <div className="md:col-span-2 pt-2">
                <button type="submit" disabled={loading} className="btn-gold" data-testid="training-submit">
                  {loading ? "Sending…" : "Reserve My Evaluation"}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <Assessment open={asmOpen} onClose={() => setAsmOpen(false)} />
    </div>
  );
}
